'use strict';

var React = require('react'),
    update = require('react/lib/update'),
    _ = require('underscore'),
    getThumbnailUrl = require('../utils/getThumbnailUrl'),
    limitThumbnailSize = require('../utils/limitThumbnailSize'),
    shallowEqual = require('react/lib/shallowEqual');

function getThumbnailOptions(size, normalSize, originalSize, mode, normalBlur, allowEmpty) {
  var width,
      height,
      blur;

  switch (size) {
  case 'thumb':
    if (normalSize.width) {
      width = 8;
    }

    if (normalSize.height) {
      height = 8;
    }

    if (normalBlur) {
      blur = 0;
    }
    break;

  case 'small':
    if (normalSize.width) {
      width = Math.round(normalSize.width / 2);
    }

    if (normalSize.height) {
      height = Math.round(normalSize.height / 2);
    }

    if (normalBlur) {
      blur = Math.round(normalBlur / 2);
    }
    break;

  case 'normal':
    if (normalSize.width) {
      width = normalSize.width;
    }

    if (normalSize.height) {
      height = normalSize.height;
    }

    if (normalBlur) {
      blur = normalBlur;
    }
    break;

  case 'retina':
    if (normalSize.width) {
      width = normalSize.width * 2;
    }

    if (normalSize.height) {
      height = normalSize.height * 2;
    }

    if (normalBlur) {
      blur = Math.round(normalBlur * 1.5);
    }
    break;

  case 'original':
    if (normalSize.width) {
      width = originalSize.width;
    }

    if (normalSize.height) {
      height = originalSize.height;
    }

    if (normalBlur) {
      blur = 0;
    }
    break;
  }

  return _.extend(
    limitThumbnailSize({ width: width, height: height }, originalSize, mode),
    { blur: blur, mode: mode, allowEmpty: allowEmpty }
  );
}

// XXX in server mode get this from ua agent
var isFirefox = (typeof navigator !== 'undefined') ?
  /firefox/i.test(navigator.userAgent) :
  false;


/**
 * Manages state of a sequential image queue.
 *
 * Use `ResponsiveImageQueueMixin` instead if you don't need
 * fine control over requested width and if you are content
 * with specifying widths for different @media-* queries.
 *
 * Use `createFlexibleImageQueueMixin(stepSize)` instead if you need the width
 * to be exactly (or close to) DOM element's offsetWidth.
 *
 * Will load images sequentially. Requires an image in your markup:
 *
 * Specify `getNormalImageSize()` which the queue will use
 * to calculate thumbnail widths in different situations.
 *
 * <img onload={this.handleImageLoad}
 *      onError={this.handleImageError}
 *      src={this.getImageSrcUrl()} />
 *
 * Requires two methods to be defined:
 *
 * * getNormalImageSize() -> { width, height }
 * * getImageOptions: () -> {
 *   image: ImageMedia,
 *   sizes: {
 *     [thumb|small|normal|retina|original]: bool,
 *     ...
 *   }
 * }
 *
 * You can call `getBackgroundImageUrl()` to get CSS value
 * for `background-image` of a `div` but you still have to embed `img`
 * in your markup (for brevity you can use `renderLoaderImg` that renders
 * an image with display:none).
 *
 * The queue gets updated whenever the next image is loaded
 * but you can force updating it by calling `updateImageQueue`.
 */
var ImageQueueMixin = {
  getInitialState() {
    return {
      requestedImages: [],
      loadedImages: [],
      loadingImage: null
    };
  },

  hasLoadedImage() {
    return this.state.loadedImages.length > 0;
  },

  componentWillMount() {
    this.updateImageQueue();
  },

  handleImageError() {
    this.updateImageQueue();
  },

  handleImageLoad() {
    var loadingImage = this.state.loadingImage;

    setTimeout(() => {
      if (this.isMounted()) {
        this.updateImageQueue(loadingImage);
      }
    });
  },

  updateImageQueue(loadedImage) {
    var requestedImages = this._getImageQueue() || [],
        loadedImages = this.state.loadedImages,
        loadingImage;

    if (loadedImage && loadedImages.indexOf(loadedImage) === -1) {
      loadedImages = update(loadedImages, {
        $push: [loadedImage]
      });
    }

    loadedImages = _.filter(loadedImages, function (image) {
      return _.contains(requestedImages, image);
    });

    loadingImage = _.find(requestedImages, function (image) {
      return !_.contains(loadedImages, image);
    });

    if (shallowEqual(requestedImages, this.state.requestedImages) &&
        shallowEqual(loadedImages, this.state.loadedImages) &&
        shallowEqual(loadingImage, this.state.loadingImage)) {
      return;
    }

    this.setState({
      requestedImages: requestedImages,
      loadedImages: loadedImages,
      loadingImage: loadingImage
    });
  },

  getBestLoadedUrls() {
    var { requestedImages, loadedImages } = this.state;

    return requestedImages
      .slice()
      .reverse()
      .filter(url => loadedImages.indexOf(url) > -1);
  },

  getBackgroundImageUrl() {
    var bestLoadedUrls = this.getBestLoadedUrls(),
        bestLoadedUrl = bestLoadedUrls[0] || this.state.loadingImage,
        nextBestLoadedUrl = bestLoadedUrls[1];

    // Firefox flickers unless we show both previous and current image
    if (bestLoadedUrl && nextBestLoadedUrl && isFirefox) {
      return `url(${bestLoadedUrl}), url(${nextBestLoadedUrl})`;
    } else if (bestLoadedUrl) {
      return `url(${bestLoadedUrl})`;
    }
  },

  getImageSrcUrl() {
    return this.state.loadingImage || this.getBestLoadedUrls()[0];
  },

  renderLoaderImg() {
    var src = this.getImageSrcUrl();
    if (!src) {
      return;
    }

    return (
      <img onLoad={this.handleImageLoad}
           onError={this.handleImageError}
           src={src}
           style={{ display: 'none' }} />
    );
  },

  _getImageQueue() {
    var imageOptions = this.getImageOptions();
    if (!imageOptions || !imageOptions.image) {
      return;
    }

    var { image, sizes, mode, blur, allowEmpty } = imageOptions,
        originalSize = image.size || { width: 2048, height: 2048 },
        normalSize = this.getNormalImageSize();

    if (!normalSize) {
      return;
    }

    return _.chain(sizes)
      .map((enabled, size) => enabled && getThumbnailOptions(size, normalSize, originalSize, mode || 'preview', blur, allowEmpty))
      .compact()
      .uniq(options => (options.width || '') + 'x' + (options.height || ''))
      .sortBy(options => (options.width || options.height) * (options.height || options.width))
      .map(options => getThumbnailUrl(image, options))
      .value();
  }
};

module.exports = ImageQueueMixin;



/** WEBPACK FOOTER **
 ** ./mixins/ImageQueueMixin.js
 **/